import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import type { UnidadResidencial, RolUsuario, Apartamento } from "../api/authService";

export interface Usuario {
  codigo?: string;
  email: string;
  nombre?: string;
  nombreCompleto?: string;
  numeroCelular?: string;
  master?: number;
}

export interface Session {
  email: string;
  password?: string; // AGREGADO: Contraseña (⚠️ riesgo de seguridad)
  usuario?: Usuario;
  unidad?: UnidadResidencial;
  rol?: RolUsuario;
  apartamento?: Apartamento;
  token?: string;
}

interface AuthContextType {
  isHydrating: boolean;
  isAuthenticated: boolean;
  session: Session | null;
  loginFinalize: (payload: {
    email: string;
    password?: string; // AGREGADO
    usuario?: Usuario;
    unidad?: UnidadResidencial;
    rol?: RolUsuario;
    apartamento?: Apartamento;
    token?: string;
  }) => Promise<void>;
  setToken: (token: string | null) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const saveSession = (session: Session) => {
  localStorage.setItem('auth_session', JSON.stringify(session));
};

const loadSession = (): Session | null => {
  try {
    const stored = localStorage.getItem('auth_session');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const clearSession = () => {
  localStorage.removeItem('auth_session');
  localStorage.removeItem('token');
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isHydrating, setIsHydrating] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  const isAuthenticated = !!session;

  const hydrate = useCallback(async () => {
    setIsHydrating(true);
    try {
      const stored = loadSession();
      if (stored) {
        setSession(stored);
        if (stored.token) {
          localStorage.setItem('token', stored.token);
        }
      }
    } catch (error) {
      console.error("Error al hidratar la sesión:", error);
    } finally {
      setIsHydrating(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const loginFinalize = useCallback(async (payload: {
    email: string;
    password?: string; // AGREGADO
    usuario?: Usuario;
    unidad?: UnidadResidencial;
    rol?: RolUsuario;
    apartamento?: Apartamento;
    token?: string;
  }) => {
    const nextSession: Session = {
      email: payload.email,
      password: payload.password, // AGREGADO
      usuario: payload.usuario,
      unidad: payload.unidad,
      rol: payload.rol,
      apartamento: payload.apartamento,
      token: payload.token,
    };

    setSession(nextSession);
    saveSession(nextSession);
    
    if (payload.token) {
      localStorage.setItem('token', payload.token);
    }
  }, []);

  const setToken = useCallback(async (token: string | null) => {
    if (!session) return;
    
    const nextSession: Session = { ...session, token: token ?? undefined };
    setSession(nextSession);
    saveSession(nextSession);
    
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [session]);

  const logout = useCallback(async () => {
    setSession(null);
    clearSession();
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    isHydrating,
    isAuthenticated,
    session,
    loginFinalize,
    setToken,
    logout,
    hydrate,
  }), [isHydrating, isAuthenticated, session, loginFinalize, setToken, logout, hydrate]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}